// OPTIONS HEDGING · Settings (opened from the gear on the Hedging page). The owner's capital pools
// and the DEFAULTS a new trade copies at creation (a running trade keeps its own copy - its exit
// settings are edited on the trade card). Basic settings first; every engineering control sits
// under "Advanced". Every value is validated again by the backend; the pool can never be set below
// what running trades already hold.
import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { px } from "@/components/options/OptionsCommon";
import { useAdvanced } from "@/lib/advanced";

const BASIC = [
  ["min_opportunity", "Spread to Maintain (Offer Opportunity per unit ≥)", "number"],
  ["max_lots", "Max pairs per trade", "int"],
  ["profit_target", "Target Profit ₹ (blank = off)", "nullable"],
  ["risk_free_rate_pct", "Risk-free rate % (valuation only)", "number"],
  ["stop_loss", "Stop loss ₹ (blank = off)", "nullable"],
];
const ADVANCED = [
  ["min_oi_execution", "Minimum OI (both legs)", "int"],
  ["reserve_pct", "Reserve % of allocated fund", "number"],
  ["exit_before_expiry_days", "Pre-expiry exit: trading days before expiry", "int"],
  ["exit_time_ist", "Pre-expiry exit time (IST)", "time"],
  ["unhedged_policy", "If only one leg fills", "policy"],
  ["leg1_timeout_s", "Leg 1 timeout (s)", "int"],
  ["leg2_max_wait_s", "Leg 2 max wait (s)", "int"],
  ["max_leg1_retries", "Leg 1 retries", "int"],
  ["max_leg2_retries", "Leg 2 remainder retries", "int"],
  ["min_dte_to_enter", "Minimum days to expiry to enter (0 = off)", "int"],
  ["min_hold_before_exit_min", "Minimum minutes before the pre-expiry exit", "int"],
  ["max_depth_age_s", "Max order-book age (s)", "int"],
  ["max_leg_spread_pct", "Max leg spread %", "number"],
  ["exit_wait_s", "Exit wait before re-price (s)", "int"],
];
const FIELDS = [...BASIC, ...ADVANCED];
const inputCls = "bg-[var(--term-input)] border border-[var(--term-border-strong)] rounded-sm px-2 py-1 w-28 mono";
const btn = "px-2 py-1 rounded-sm text-xs font-bold border";

function toForm(s) {
  const f = { pool_SHADOW: String(s.pool_limit?.SHADOW ?? 0), pool_LIVE: String(s.pool_limit?.LIVE ?? 0) };
  FIELDS.forEach(([k]) => { const v = s.trade_defaults?.[k]; f[k] = v === null || v === undefined ? "" : String(v); });
  return f;
}

function toBody(f) {
  const td = {};
  FIELDS.forEach(([k, , kind]) => {
    const v = f[k];
    if (kind === "nullable") td[k] = v === "" ? null : Number(v);
    else if (kind === "time" || kind === "policy") td[k] = v;
    else td[k] = Number(v);
  });
  return { pool_limit: { SHADOW: Number(f.pool_SHADOW), LIVE: Number(f.pool_LIVE) }, trade_defaults: td };
}

function Rows({ list, form, set }) {
  return (
    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1">
      {list.map(([k, label, kind]) => (
        <label key={k} className="flex items-center gap-2 justify-between" data-testid={`hedge-setting-${k}`}>
          <span className="text-[var(--term-muted)]">{label}</span>
          {kind === "policy"
            ? <select className={inputCls} value={form[k]} onChange={set(k)}><option value="PARK">Wait for me (PARK)</option><option value="AUTO_UNWIND">Sell it automatically (AUTO_UNWIND)</option></select>
            : <input className={inputCls} value={form[k]} onChange={set(k)} placeholder={kind === "nullable" ? "off" : kind === "time" ? "HH:MM" : ""} />}
        </label>))}
    </div>
  );
}

export default function HedgeSettings({ open, onClose, environments = ["SHADOW", "LIVE"], showCalendar = true }) {
  const [form, setForm] = useState(null);
  const [funds, setFunds] = useState({});
  const [cal, setCal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adv, setAdv] = useState(false);
  const [showAdvanced, setShowAdvanced] = useAdvanced();
  const load = () => {
    api.get("/options-hedge/settings").then((r) => setForm(toForm(r.data))).catch((e) => toast.error(apiError(e)));
    ["SHADOW", "LIVE"].forEach((env) => api.get("/options-hedge/funds", { params: { environment: env } })
      .then((r) => setFunds((p) => ({ ...p, [env]: r.data }))).catch(() => {}));
    if (showCalendar) api.get("/options-hedge/calendar").then((r) => setCal(r.data)).catch(() => setCal(null));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [open]);
  const save = async () => {
    setSaving(true);
    try { await api.put("/options-hedge/settings", toBody(form)); toast.success("Options Hedging settings saved"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const refreshCal = async () => {
    try { const r = await api.post("/options-hedge/calendar/refresh"); toast.success(`Holiday calendar refreshed (${r.data.holidays} holidays, ${r.data.years.join(", ")})`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  if (!open) return null;
  return (
    <div className="term-panel rounded-sm p-3 text-xs space-y-3" data-testid="hedge-settings">
      <div className="flex items-center gap-2">
        <b className="text-sm">Settings</b>
        <span className="text-[10px] text-[var(--term-muted)]">your capital pool and the defaults new trades start with</span>
        {onClose && <button className="ml-auto" onClick={onClose} aria-label="Close settings" data-testid="hedge-settings-close"><X className="h-4 w-4" /></button>}
      </div>
      {!form ? <div className="text-[var(--term-muted)]">Loading…</div> : <>
        <div className="grid sm:grid-cols-2 gap-2">
          {environments.map((env) => (
            <div key={env} className="term-panel-2 rounded-sm p-2 space-y-1" data-testid={`hedge-pool-${env}`}>
              <div className="font-bold">{env} capital pool ₹</div>
              <input className={inputCls} value={form[`pool_${env}`]} onChange={set(`pool_${env}`)} data-testid={`hedge-pool-input-${env}`} />
              <div className="text-[10px] text-[var(--term-muted)] mono">
                in use {px(funds[env]?.reserved)} · available {funds[env]?.headroom === null || funds[env]?.headroom === undefined ? (env === "SHADOW" ? "not limited (SHADOW)" : "limited by broker funds at Start") : px(funds[env]?.headroom)}
                {env === "SHADOW" ? " · notional (no real money)" : " · also limited by Zerodha's available funds"}
              </div>
            </div>))}
        </div>
        <Rows list={BASIC} form={form} set={set} />
        <button className="flex items-center gap-1 text-[11px] text-[var(--term-muted)] hover:text-[var(--term-primary)]" onClick={() => setAdv(!adv)}
          data-testid="hedge-settings-advanced-toggle">{adv ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} Advanced</button>
        {adv && <div className="space-y-2" data-testid="hedge-settings-advanced">
          <Rows list={ADVANCED} form={form} set={set} />
          {showCalendar && cal && <div className="term-panel-2 rounded-sm p-2 flex items-center gap-2 flex-wrap" data-testid="hedge-calendar">
            <b>NFO trading calendar</b>
            <span className={cal.covers_this_year ? "text-[#008a04]" : "text-[#FF3B30]"}>{cal.covers_this_year ? "covers this year" : "DOES NOT cover this year"}</span>
            <span className="mono text-[10px] text-[var(--term-muted)]">years {(cal.years || []).join(", ") || "—"} · {cal.holidays} holidays · NSE holiday master</span>
            <button className={`${btn} ml-auto`} onClick={refreshCal} data-testid="hedge-calendar-refresh">Refresh from NSE</button>
          </div>}
        </div>}
        <div className="flex items-center gap-2">
          <button className={`${btn} border-[#2563EB] text-[#2563EB]`} disabled={saving} onClick={save} data-testid="hedge-settings-save">{saving ? "Saving…" : "Save settings"}</button>
          <span className="text-[10px] text-[var(--term-muted)]">Allowed underlyings, open-trade and daily-loss limits are set by an administrator.</span>
          <label className="ml-auto flex items-center gap-1 text-[11px]" title="Show readiness details, status codes and Diagnostics on the Options screens">
            <input type="checkbox" checked={showAdvanced} onChange={(e) => setShowAdvanced(e.target.checked)} data-testid="hedge-show-advanced" /> Show advanced details</label>
        </div>
      </>}
    </div>
  );
}
